import {
  Button,
  HStack,
  Spacer,
  Stack,
  ButtonGroup,
  Box,
  IconButton,
  Icon,
  NativeSelect,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useState, useMemo, ChangeEvent } from "react";
import { t } from "i18next";
import {
  CraAttribute,
  CraAttributes,
  CraAttributeName,
  DistanceUnit,
} from "@keita/lanepatrol-sdk";
import { CraAttributeUtils } from "../../../../../../coding/utils/attribute-utils";
import { parseAttributeInputValue } from "../../../../../../coding/utils/attribute-value";
import { CraForecastChangeType } from "../../../../../../survey/models/CraForecastScenario";
import { AttributeInput } from "../../../../../../shared/views/AttributeInput/AttributeInput";
import { CraNode } from "../../../../../../cra/models/CraNode";
import { useStores } from "../../../../../../shared/stores/RootStore";
import { CloseIcon } from "../../../../../../shared/views/icons";
import { formatDistanceByUnit } from "../../../../../../shared/utils/format-utils";

interface State {
  attribute: CraAttribute | undefined;
  value: string | null;
}
interface ValidatedState {
  attribute: CraAttribute;
  value: string;
}

interface Props {
  filteredDistance: number;
  filteredNodes: CraNode[];
  onClose: () => void;
}
const AddAttributeValuesActionBase: React.FC<Props> = ({
  filteredDistance,
  filteredNodes,
  onClose,
}) => {
  const { craForecastStore, surveyStore } = useStores();
  const distanceUnit = surveyStore.distanceUnit;

  const [state, setState] = useState<State[]>([
    {
      attribute: undefined,
      value: null,
    },
  ]);

  const validItems = useMemo(
    () =>
      state.filter(
        ({ attribute, value }) =>
          attribute && parseAttributeInputValue(attribute, value) !== null,
      ) as ValidatedState[],
    [state],
  );

  const onChangeCraAttribute =
    (index: number) => (e: ChangeEvent<HTMLSelectElement>) => {
      const attributeName = e.target.value as CraAttributeName;
      setState((prev) =>
        prev.map((item, i) =>
          i === index
            ? {
                attribute: CraAttributes.getByName(attributeName),
                value: null,
              }
            : item,
        ),
      );
    };

  const onChangeAttributeValue = (index: number) => (value: string | null) => {
    setState((prev) =>
      prev.map((item, i) => (i === index ? { ...item, value: value } : item)),
    );
  };

  const onSubmit = () => {
    craForecastStore.addAction({
      type: CraForecastChangeType.AttributeValues,
      attributeValues: Object.fromEntries(
        validItems.map(({ attribute, value }) => [attribute.name, value]),
      ),
    });

    onClose();
  };

  const addItem = () => {
    setState((prev) =>
      prev.concat({
        attribute: undefined,
        value: null,
      }),
    );
  };

  const removeItem = (index: number) => {
    setState((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Stack>
      {state.map(({ attribute, value }, i) => (
        <HStack pb={2} key={i}>
          <NativeSelect.Root flex={1}>
            <NativeSelect.Field
              onChange={onChangeCraAttribute(i)}
              value={attribute?.name || ""}
              placeholder={t(
                `map:forecast.AttributeValues.attributeNamePlaceholder`,
              )}
            >
              {CraAttributes.all.map((attribute) => {
                if (attribute.name === CraAttributeName.Comment) return null;
                return (
                  <option key={attribute.name} value={attribute.name}>
                    {CraAttributeUtils.getLabel(attribute.name)}
                  </option>
                );
              })}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          {attribute && (
            <Box flex={1}>
              <AttributeInput
                attribute={attribute}
                value={value}
                setValue={onChangeAttributeValue(i)}
                allowNullValues
              />
            </Box>
          )}

          <IconButton
            variant="ghost"
            size="sm"
            colorPalette="primary"
            disabled={state.length === 1}
            onClick={() => removeItem(i)}
          >
            <Icon as={CloseIcon} />
          </IconButton>
        </HStack>
      ))}
      <HStack>
        <Spacer />
        <ButtonGroup size="sm" colorPalette="primary">
          <Button variant="ghost" onClick={addItem}>
            {t(`map:forecast.AttributeValues.addBtn`)}
          </Button>
          <Button
            flexShrink={0}
            onClick={onSubmit}
            disabled={!validItems.length || !filteredNodes.length}
          >
            {t(`map:forecast.addActionModal.applyBtn`, {
              count: filteredNodes.length,
              total: formatDistanceByUnit(filteredDistance, distanceUnit),
              unit: distanceUnit === DistanceUnit.Imperial ? "mi" : "km",
            })}
          </Button>
        </ButtonGroup>
      </HStack>
    </Stack>
  );
};

export const AddAttributeValuesAction = observer(AddAttributeValuesActionBase);
